import React, { useState } from 'react';
import TabContext from '@mui/lab/TabContext';

import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { TabList, TabPanel } from '@mui/lab';
import { Divider, Tab } from '@mui/material';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { ArticleOutlined } from '@mui/icons-material';
import { WebhookData } from '@common/Node/NodeSidebar/NodeData';
import { useTheme } from '../../../../../styles';
import { AnswerNodeState } from '../../types';
import { ToggleTranscription } from '@common/Node/NodeSidebar/Custom/ToggleTranscription';
import { CallHeaders } from '@common/Node/NodeSidebar/Custom/CallHeaders';
import { Toggle } from '@common/Node/NodeSidebar/Custom/Toggle';
import { useParams } from 'react-router-dom';

export const AnswerCallTabs: React.FC = () => {
  const theme = useTheme();

  const { workflow_id } = useParams();
  const [tab, setTab] = useState('1');

  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);
  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  const themeType = useSelector((state: State) => state.flow.theme);

  if (!command) return;

  const fields = command.fields;
  const inputs = fields.filter((field) => field.tab === '1' && field.id !== 'transcription');

  const tabColor = themeType === 'light' ? theme.text.standard : theme.telnyx.citron;
  const state = command.state as AnswerNodeState;

  // const recordInputs = fields.filter((field) => {
  //   const isRecordingEnabled = state.record === 'record-from-answer';
  //   if (field.id !== 'record' && !isRecordingEnabled && tab === '3') return false;
  //   if (field.tab === '3') return true;
  //   return false;
  // });

  const isStreamUrlDefined = state?.stream_url?.value;
  const isStreamModeRTP = state?.stream_bidirectional_mode === 'rtp';
  const streamUrl = fields.find((field) => field.id === 'stream_url');
  const streamTrack = fields.find((field) => field.id === 'stream_track');
  const streamBidirectionalMode = fields.find((field) => field.id === 'stream_bidirectional_mode');
  const streamBidirectionalCodec = fields.find((field) => field.id === 'stream_bidirectional_codec');
  const streamBidirectionalTargetLegs = fields.find((field) => field.id === 'stream_bidirectional_target_legs');

  const transcriptionInput = fields.find((field) => field.id === 'transcription');
  const isTranscriptionEnabled = state?.transcription?.value === 'true';
  const interruptPlayback = fields.find((field) => field.id === 'interrupt_playback');
  const transcriptionEngine = fields.find((field) => field.id === 'transcription_engine');
  const transcriptionTracks = fields.find((field) => field.id === 'transcription_tracks');
  const telnyxLanguageOptions = fields.find((field) => field.id === 'language_telnyx');
  const googleLanguageOptions = fields.find((field) => field.id === 'language_google');
  const isGoogleTranscription = isTranscriptionEnabled && state?.transcription_engine?.value === 'A';
  const isTelnyxTranscription = isTranscriptionEnabled && state?.transcription_engine?.value === 'B';

  const customHeaders = fields.find((field) => field.id === 'custom_headers');
  const sipHeaders = fields.find((field) => field.id === 'sip_headers');

  const soundModificationEnabled = fields.find((field) => field.id === 'sound_modifications_enabled');
  const isSoundModsEnabled = state?.sound_modifications_enabled === 'true';
  const soundModFields = fields.filter((field) => field.tab === 'sound_modifications');

  const secondTabError = nodeErrors?.find(
    (nodeError) => nodeError?.workflowId === workflow_id && nodeError.fields.some((field) => field.tab === '2')
  );

  return (
    <NodeSidebar>
      <TabContext value={tab}>
        <NodeSidebarTabsContainer color={tabColor}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Node' value='1' />
            <Tab
              label='Headers'
              value='2'
              sx={{ color: Boolean(secondTabError) ? `${theme.telnyx.red} !important` : undefined }}
            />
            <Tab title='Data' disableRipple iconPosition='start' icon={<ArticleOutlined />} value='3' />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {inputs.map((field) => (
              <NodeInputExpression key={field.id} command={command} field={field} />
            ))}
            {streamUrl && <Divider />}
            {streamUrl && <NodeInputExpression key={streamUrl.id} command={command} field={streamUrl} />}
            {isStreamUrlDefined && streamTrack && (
              <NodeInputExpression key={streamTrack.id} command={command} field={streamTrack} />
            )}
            {isStreamUrlDefined && streamBidirectionalMode && (
              <NodeInputExpression key={streamBidirectionalMode.id} command={command} field={streamBidirectionalMode} />
            )}
            {isStreamUrlDefined && isStreamModeRTP && streamBidirectionalCodec && (
              <NodeInputExpression
                key={streamBidirectionalCodec.id}
                command={command}
                field={streamBidirectionalCodec}
              />
            )}
            {isStreamUrlDefined && streamBidirectionalTargetLegs && (
              <NodeInputExpression
                key={streamBidirectionalTargetLegs.id}
                command={command}
                field={streamBidirectionalTargetLegs}
              />
            )}
            {soundModificationEnabled && <Divider />}
            {soundModificationEnabled && (
              <Toggle key={soundModificationEnabled.id} command={command} field={soundModificationEnabled} />
            )}
            {isSoundModsEnabled &&
              soundModFields.map((field) => <NodeInputExpression key={field.id} command={command} field={field} />)}
            {soundModificationEnabled && <Divider />}
            {transcriptionInput && (
              <ToggleTranscription key={transcriptionInput.id} command={command} field={transcriptionInput} />
            )}
            {transcriptionEngine && isTranscriptionEnabled && (
              <NodeInputExpression key={transcriptionEngine.id} command={command} field={transcriptionEngine} />
            )}
            {isGoogleTranscription && googleLanguageOptions && (
              <NodeInputExpression key={googleLanguageOptions.id} command={command} field={googleLanguageOptions} />
            )}
            {isTelnyxTranscription && telnyxLanguageOptions && (
              <NodeInputExpression key={telnyxLanguageOptions.id} command={command} field={telnyxLanguageOptions} />
            )}
            {transcriptionTracks && isTranscriptionEnabled && (
              <NodeInputExpression key={transcriptionTracks.id} command={command} field={transcriptionTracks} />
            )}
            {isGoogleTranscription && interruptPlayback && (
              <NodeInputExpression key={interruptPlayback.id} command={command} field={interruptPlayback} />
            )}
            <Divider sx={{ marginBottom: '4rem' }} />
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='2'>
          <NodeSidebarTab>
            {customHeaders && (
              <CallHeaders key={customHeaders.id} field={customHeaders} command={command} headerType='custom' />
            )}
            {customHeaders && sipHeaders && <Divider />}
            {sipHeaders && <CallHeaders key={sipHeaders.id} field={sipHeaders} command={command} headerType='sip' />}
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='3'>
          <WebhookData command={command} />
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
